// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter, JSX } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import {
  inheritAriaAttributes,
  pickFocusEventAttributes,
  resolveTarget,
} from '../../helpers'
import type { Importance, Size, Status } from '../shared'

export type Action = 'closable' | 'expandable'

export type Placement = 'bottom' | 'left' | 'right' | 'top'

export type TagStatus = Status | 'neutral'

export type IndicatorPosition =
  | 'block-end'
  | 'block-start'
  | 'inline-end'
  | 'inline-start'

const isCounter = (value: unknown): value is number =>
  typeof value === 'string'
    ? !Number.isNaN(Number.parseFloat(value))
    : typeof value === 'number'

const isIndicator = (value: unknown): value is '' | true =>
  value === '' || value === true

let autoIncrementingId = 1

/**
 * <qds-tab> is a navigational element that is used in different horizontal or vertical navigation bars
 * (Main Navigation, secondary navigation, Tab bar, etc.)
 * as trigger points for opening a menu list or switching content in Tab bar.
 *
 * @see https://quartz.se.com/build/components/tab
 */
@Component({
  tag: 'qds-tab',
  shadow: true,
  styleUrl: 'tab.css',
})
export class Tab implements ComponentInterface {
  /**
   * The name of the action icon to render.
   */
  @Prop() public readonly action?: Action

  /**
   * Displays an indicator if `true` or the empty string. Otherwise, displays a
   * counter badge if a number or string representing a number is used.
   */
  @Prop() public readonly badge?: number | string | true

  /**
   * The tab's counter importance.
   */
  @Prop()
  public readonly badgeCounterImportance?: HTMLQdsBadgeCounterElement['importance'] =
    'standard'

  /**
   * The indicator or counter badge's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly badgeDescription?: string

  /**
   * The indicator's status.
   */
  @Prop()
  public readonly badgeIndicatorStatus?: HTMLQdsBadgeIndicatorElement['status'] =
    'neutral'

  /**
   * Prevents the tab from being interacted with: it cannot be
   * selected or focused.
   */
  @Prop() public readonly disabled?: boolean

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * Displays the tab in a collapsed state without its text. The text will be
   * displayed in a tooltip instead.
   *
   * This property has no effect if the tab does not have an icon set.
   */
  @Prop() public readonly iconOnly: boolean = false

  /**
   * The tab's importance.
   */
  @Prop() public readonly importance?: Importance = 'standard'

  /**
   * The tab's indicator position.
   */
  @Prop() public readonly indicatorPosition?: IndicatorPosition = 'block-end'

  /**
   * The tab panel element associated with this tab. The `panel` specified
   * can be any of the following:
   *
   * - A CSS selector string
   * - A reference to an [`Element`][]
   * - A function returning a reference to an [`Element`][] or a CSS selector
   * string
   *
   * [`Element`]: https://developer.mozilla.org/docs/Web/API/Element
   */
  @Prop() public readonly panel!: Element | string | (() => Element | string)

  /**
   * The tag icon's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly tagIconDescription?: string

  /**
   * The name of the tab's tag icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly tagIconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly tagIconLibrary: string = 'default'

  /**
   * The tab's tag importance.
   */
  @Prop() public readonly tagImportance?: HTMLQdsTagElement['importance'] =
    'emphasized'

  /**
   * The tab's tag size.
   */
  @Prop() public readonly tagSize?: Size = 'small'

  /**
   * The tab's tag status.
   */
  @Prop() public readonly tagStatus?: Status = 'info'

  /**
   * The tab's tag text.
   */
  @Prop() public readonly tagText?: string

  /**
   * Whether or not the tab is selected.
   */
  @Prop() public selected = false

  /**
   * The tab's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Text to display for the subtitle.
   */
  @Prop() public readonly subtitle?: string

  /**
   * Primary text to display.
   */
  @Prop() public readonly text!: string

  /**
   * When set, the underlying tab will be rendered as an `<a>` with this
   * `href` instead of a `<button>`.
   *
   * This property has no effect if the tab has an `action`.
   *
   * @webnative
   */
  @Prop() public readonly href?: string

  /**
   * Emitted when the tab is closed.
   */
  @Event({ eventName: 'qdsClose', cancelable: false })
  private readonly closeEmitter!: EventEmitter<void>

  /**
   * Emitted when the tab loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the tab gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  @State() private isIndicatorOnTop = false

  #inheritedAttributes: Attributes = {}

  #div?: HTMLDivElement

  #panel?: Element

  get #collapsed(): boolean {
    const hasNoTag = this.tagText === undefined || this.tagText === ''
    const hasNoBadge = this.badge === undefined || this.badge === ''

    return (
      this.#shouldCollapse &&
      this.#computedAction === undefined &&
      hasNoTag &&
      hasNoBadge
    )
  }

  get #computedAction(): Action | undefined {
    switch (this.action) {
      case 'closable':
      case 'expandable': {
        return this.action
      }
      default: {
        return undefined
      }
    }
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedImportance(): Importance {
    switch (this.importance) {
      case 'subdued':
      case 'emphasized': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #parentLayer(): string {
    return this.host.closest('qds-tabbar')?.layer ?? 'main'
  }

  get #parentLevel(): string {
    return this.host.closest('qds-tabbar')?.level ?? 'root'
  }

  get #ariaLabel(): string {
    return `Close ${this.text}`
  }

  get #computedDisabled(): boolean {
    return this.disabled ?? false
  }

  get #shouldCollapse(): boolean {
    return this.iconOnly && this.iconName !== undefined && this.iconName !== ''
  }

  get #placement(): Placement {
    switch (this.indicatorPosition) {
      case 'inline-start': {
        return 'left'
      }
      case 'inline-end': {
        return 'right'
      }
      case 'block-start': {
        return 'top'
      }
      default: {
        return 'bottom'
      }
    }
  }

  @Watch('panel')
  protected panelChanged(): void {
    this.disconnectedCallback()

    const panel = resolveTarget(this.panel)
    if (!(panel instanceof globalThis.Element)) return
    this.#panel = panel

    const tab = this.host.shadowRoot?.querySelector('[role="tab"]')
    if (tab) tab.setAttribute('aria-controls', this.#panel.id)

    if (this.#panel.role === null) this.#panel.role = 'tabpanel'
    if (this.#panel.getAttribute('aria-labelledby') === null)
      this.#panel.setAttribute('aria-labelledby', this.host.id)
    this.#panel.setAttribute(
      'tabindex',
      this.#panel.getAttribute('tabindex') ?? '0',
    )
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) event.stopImmediatePropagation()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
    this.host.id ||= `qds-tab-${autoIncrementingId}`
    autoIncrementingId += 1
  }

  public componentWillRender(): void {
    const isPanelRoot =
      this.#parentLayer === 'panel' && this.#parentLevel === 'root'
    const isMainSubsection =
      this.#parentLayer === 'main' && this.#parentLevel === 'subsection'

    this.isIndicatorOnTop = isPanelRoot || isMainSubsection
  }

  public componentDidLoad(): void {
    this.panelChanged()
  }

  public disconnectedCallback(): void {
    if (!this.#panel) return

    const tab = this.host.shadowRoot?.querySelector('[role="tab"]')
    if (tab) tab.removeAttribute('aria-controls')

    if (this.#panel.role === 'tabpanel')
      // eslint-disable-next-line unicorn/no-null
      this.#panel.role = null
    if (this.#panel.getAttribute('aria-labelledby') === this.host.id)
      this.#panel.removeAttribute('aria-labelledby')
    this.#panel.removeAttribute('tabindex')
  }

  public render() {
    const isLink = this.href !== undefined && this.#computedAction === undefined
    const indicatorPosition = this.isIndicatorOnTop
      ? 'block-start'
      : this.indicatorPosition
    const Tag = isLink
      ? 'a'
      : this.#computedAction === 'expandable'
        ? 'button'
        : 'div'
    return (
      <div
        aria-disabled={this.#computedDisabled ? 'true' : undefined}
        class={{
          'qds-closable-wrapper': this.#computedAction === 'closable',
          'qds-selected': this.selected,
          [`qds-indicator-${indicatorPosition}`]:
            this.#computedAction === 'closable',
        }}
        data-size={this.#computedSize}
        data-importance={this.#computedImportance}
        ref={this.#ref}
      >
        <Tag
          aria-disabled={this.#computedDisabled ? 'true' : undefined}
          disabled={isLink ? undefined : this.disabled}
          onBlur={this.#onBlur}
          onFocus={this.#onFocus}
          class={{
            'qds-tab': this.#computedAction !== 'closable',
            'qds-icon-only': this.#collapsed,
            'qds-collapse':
              this.#shouldCollapse && this.#computedAction !== 'closable',
            'qds-closable-tab': this.#computedAction === 'closable',
            'qds-selected': this.selected,
            [`qds-indicator-${this.indicatorPosition}`]:
              this.#computedAction !== 'closable',
          }}
          role={
            !isLink && this.#computedAction !== 'expandable' ? 'tab' : undefined
          }
          aria-current={isLink && this.selected ? 'true' : undefined}
          aria-expanded={
            this.#computedAction === 'expandable' && !isLink
              ? this.selected
                ? 'true'
                : 'false'
              : undefined
          }
          data-importance={this.#computedImportance}
          data-action={this.#computedAction}
          data-size={this.#computedSize}
          href={isLink ? this.href : undefined}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.#inheritedAttributes}
        >
          {((this.tagText !== undefined && this.tagText !== '') ||
            (this.tagIconName !== undefined && this.tagIconName !== '')) && (
            <qds-tag
              text={this.tagText}
              status={this.tagStatus}
              size={this.#computedSize}
              importance={this.tagImportance}
              icon-library={this.tagIconLibrary}
              icon-name={this.tagIconName}
              icon-description={this.tagIconDescription}
              class={{ 'qds-hidden': this.#shouldCollapse }}
            />
          )}

          {this.iconName !== undefined && this.iconName !== '' && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon"
              library={this.iconLibrary}
              name={this.iconName}
            />
          )}
          {!this.#shouldCollapse && this.text && this.#renderText()}
          {isIndicator(this.badge) && (
            <qds-badge-indicator
              class={{
                'qds-indicator': this.#shouldCollapse,
              }}
              status={this.badgeIndicatorStatus}
              description={this.badgeDescription}
              size={this.#computedSize}
              strokeRing
            />
          )}
          {isCounter(this.badge) && (
            <qds-badge-counter
              class={{
                'qds-indicator': this.#shouldCollapse,
              }}
              description={this.badgeDescription}
              importance={this.badgeCounterImportance}
              size={this.#computedSize}
              strokeRing
              value={this.badge}
            />
          )}
          {this.#computedAction === 'expandable' && (
            <qds-icon
              aria-hidden="true"
              class={{ 'qds-target-icon': true, 'qds-chevron': this.selected }}
              data-size={this.#computedSize}
              library="core"
              name="dropdown"
            />
          )}
        </Tag>
        {this.#computedAction === 'closable' && (
          <button
            aria-label={this.#ariaLabel}
            class="qds-target"
            tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
            disabled={this.disabled}
            onClick={this.#onIconClick}
            onKeyDown={this.#onIconKeyDown}
            type="button"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.#inheritedAttributes}
          >
            <qds-icon
              aria-hidden="true"
              class="qds-target-icon"
              data-size={this.#computedSize}
              library="core"
              name="close"
            />
          </button>
        )}
        {this.#shouldCollapse && this.text && (
          <qds-tooltip placement={this.#placement} ref={this.#tooltipRef}>
            {this.#renderText()}
          </qds-tooltip>
        )}
      </div>
    )
  }

  #renderText(): JSX.Element {
    return this.#shouldCollapse ? (
      <qds-title subtitle={this.subtitle} layer="accessory" level="root">
        {this.text}
      </qds-title>
    ) : (
      <div class="qds-texts">
        <div class="qds-text">{this.text}</div>
        {this.subtitle !== undefined && this.subtitle !== '' && (
          <div class="qds-subtitle">{this.subtitle}</div>
        )}
      </div>
    )
  }

  readonly #ref = (div?: HTMLDivElement): void => {
    this.#div = div
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#div
  }

  readonly #close = (): void => {
    this.closeEmitter.emit()
  }

  readonly #onIconClick = (event: MouseEvent): void => {
    event.stopPropagation()

    const path = event.composedPath()
    const closeIcon = path.find(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        element.classList.contains('qds-target'),
    )

    if (closeIcon && !event.defaultPrevented) this.#close()
  }

  readonly #onIconKeyDown = (event: KeyboardEvent): void => {
    const path = event.composedPath()
    const closeIcon = path.find(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        element.classList.contains('qds-target'),
    )

    if (closeIcon && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      event.stopPropagation()
      this.#close()
    }
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }
}
